import { useCallback } from 'react';
import { useDataProvider } from 'react-admin';

interface CreateSessionResponse {
  data: {
    sessionId: string;
  };
}

interface SendMessageResponse {
  data: {
    messageId: string;
    response: string;
    llmUsed: string;
    contextsLoaded: string[];
  };
}

interface FeedbackResponse {
  data: {
    shouldRetry: boolean;
    message?: string;
  };
}

interface UsageReportResponse {
  data: {
    periodo: {
      inicio: string;
      fim: string;
    };
    resumo: {
      totalSessoes: number;
      totalMensagens: number;
      totalTokens: number;
      custoTotalUsd: number;
    };
    satisfacao: {
      positivos: number;
      negativos: number;
      taxa: number;
    };
    usoPorLlm: Array<{
      llm: string;
      mensagens: number;
      custo: number;
      percentual: number;
    }>;
    contextosMaisUsados: Array<{
      contexto: string;
      vezesUsado: number;
      percentual: number;
    }>;
    topUsuarios: Array<{
      odUserId: string;
      nome: string;
      mensagens: number;
      tokens: number;
      custo: number;
      taxaSatisfacao: number;
    }>;
    deepseekBalance: {
      available: number;
      currency: string;
    } | null;
  };
}

export const useSupportAgent = () => {
  const dataProvider = useDataProvider();

  const createSession = useCallback(async (): Promise<CreateSessionResponse> => {
    return dataProvider.create('chains/chainId/support-agent/sessions', { data: {} });
  }, [dataProvider]);

  const sendMessage = useCallback(async (
    sessionId: string,
    message: string
  ): Promise<SendMessageResponse> => {
    return dataProvider.create(`chains/chainId/support-agent/sessions/${sessionId}/messages`, {
      data: { message },
    });
  }, [dataProvider]);

  const sendFeedback = useCallback(async (
    messageId: string,
    feedback: 'positive' | 'negative'
  ): Promise<FeedbackResponse> => {
    return dataProvider.create(`chains/chainId/support-agent/messages/${messageId}/feedback`, {
      data: { feedback },
    });
  }, [dataProvider]);

  // Relatório usa getList (backend retorna array com 1 elemento)
  const getUsageReport = useCallback(async (
    from: string,
    to: string,
    userId?: string,
    chainId?: string
  ): Promise<UsageReportResponse> => {
    const filter: Record<string, string> = { from, to };
    if (userId) filter.userId = userId;

    // Usa chainId específico ou rota global (sem chainId) para admins
    const resource = chainId
      ? `chains/${chainId}/support-agent/reports/usage`
      : 'support-agent/reports/usage';

    const result = await dataProvider.getList(resource, {
      filter,
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'ASC' },
    });
    // Pega primeiro elemento do array
    return { data: result.data[0] } as UsageReportResponse;
  }, [dataProvider]);

  return {
    createSession,
    sendMessage,
    sendFeedback,
    getUsageReport,
  };
};

export default useSupportAgent;
